import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  User,
  Clock,
  Search,
  Filter,
  Download,
  MoreHorizontal,
  Edit,
  Eye,
  Mail,
  Phone,
  MapPin,
  Calendar,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  FileText,
  Camera,
  Award,
  Users,
  TrendingUp,
  UserCheck,
  Loader
} from 'lucide-react'
import { format } from 'date-fns'
import ApiService from '../services/api'
import toast from 'react-hot-toast'

function UnverifiedPandits() {
  const navigate = useNavigate()
  const [unverifiedPandits, setUnverifiedPandits] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [verifying, setVerifying] = useState({})
  const [rejecting, setRejecting] = useState({})
  const [requestingInfo, setRequestingInfo] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [filterExperience, setFilterExperience] = useState('all')
  const [filterLocation, setFilterLocation] = useState('all')
  const [selectedPandits, setSelectedPandits] = useState(new Set())
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  })
  const [stats, setStats] = useState({
    totalUnverified: 0,
    pendingThisWeek: 0,
    avgProcessingTime: 0,
    urgentReviews: 0
  })

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 500)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchTerm, filterExperience, filterLocation])

  // Load unverified pandits data
  useEffect(() => {
    fetchUnverifiedPandits()
  }, [currentPage, debouncedSearchTerm, filterExperience, filterLocation])

  const fetchUnverifiedPandits = async () => {
    try {
      setLoading(true)
      setError(null)

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(filterExperience !== 'all' && { experience: filterExperience }),
        ...(filterLocation !== 'all' && { state: filterLocation }),
        sortBy: 'createdAt',
        sortOrder: 'desc'
      })

      const response = await ApiService.getUnverifiedPandits(params)

      if (response.success) {
        setUnverifiedPandits(response.data || [])
        setPagination(response.pagination || {})

        // Calculate stats
        const pandits = response.data || []
        const oneWeekAgo = new Date()
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)

        const recentPandits = pandits.filter(p => new Date(p.createdAt) >= oneWeekAgo)
        const urgentPandits = pandits.filter(p => {
          const daysSinceRegistration = Math.floor((new Date() - new Date(p.createdAt)) / (1000 * 60 * 60 * 24))
          return daysSinceRegistration > 7
        })

        setStats({
          totalUnverified: response.pagination?.total || pandits.length,
          pendingThisWeek: recentPandits.length,
          avgProcessingTime: 3, // Mock average processing time
          urgentReviews: urgentPandits.length
        })
      } else {
        throw new Error(response.message || 'Failed to fetch unverified pandits')
      }
    } catch (error) {
      console.error('Error fetching unverified pandits:', error)
      setError(error.message || 'Failed to load unverified pandits')
      toast.error('Failed to load unverified pandits')
      setUnverifiedPandits([])
      setPagination({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      })
      setStats({
        totalUnverified: 0,
        pendingThisWeek: 0,
        avgProcessingTime: 0,
        urgentReviews: 0
      })
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyPandit = async (panditId) => {
    try {
      setVerifying(prev => ({ ...prev, [panditId]: true }))
      await ApiService.verifyPandit(panditId)
      toast.success('Pandit verified successfully')

      // Remove from unverified list
      setUnverifiedPandits(prev => prev.filter(p => p.id !== panditId))
      setStats(prev => ({ ...prev, totalUnverified: prev.totalUnverified - 1 }))
    } catch (error) {
      console.error('Error verifying pandit:', error)
      toast.error('Failed to verify pandit')
    } finally {
      setVerifying(prev => ({ ...prev, [panditId]: false }))
    }
  }

  const handleBulkVerify = async () => {
    if (selectedPandits.size === 0) {
      toast.error('Please select pandits to verify')
      return
    }

    try {
      const promises = Array.from(selectedPandits).map(panditId =>
        ApiService.verifyPandit(panditId)
      )
      await Promise.all(promises)

      toast.success(`${selectedPandits.size} pandits verified successfully`)
      setSelectedPandits(new Set())
      fetchUnverifiedPandits() // Refresh the list
    } catch (error) {
      console.error('Error in bulk verification:', error)
      toast.error('Some verifications failed. Please try again.')
    }
  }

  const handleViewDocuments = (panditId) => {
    navigate(`/pandits/${panditId}`)
  }

  const handleRequestMoreInfo = async (panditId) => {
    const message = prompt('Enter the information you need from this pandit:')
    if (!message || message.trim() === '') {
      return
    }

    try {
      setRequestingInfo(prev => ({ ...prev, [panditId]: true }))
      await ApiService.requestMoreInfo(panditId, message)
      toast.success('Information request sent successfully')
    } catch (error) {
      console.error('Error requesting more info:', error)
      toast.error('Failed to send information request')
    } finally {
      setRequestingInfo(prev => ({ ...prev, [panditId]: false }))
    }
  }

  const handleRejectApplication = async (panditId) => {
    const reason = prompt('Enter the reason for rejection:')
    if (!reason || reason.trim() === '') {
      return
    }

    const confirmed = window.confirm('Are you sure you want to reject this application? This action cannot be undone.')
    if (!confirmed) {
      return
    }

    try {
      setRejecting(prev => ({ ...prev, [panditId]: true }))
      await ApiService.rejectPanditApplication(panditId, reason)
      toast.success('Application rejected successfully')

      // Remove from unverified list
      setUnverifiedPandits(prev => prev.filter(p => p.id !== panditId))
      setStats(prev => ({ ...prev, totalUnverified: prev.totalUnverified - 1 }))
    } catch (error) {
      console.error('Error rejecting application:', error)
      toast.error('Failed to reject application')
    } finally {
      setRejecting(prev => ({ ...prev, [panditId]: false }))
    }
  }

  const toggleSelectPandit = (panditId) => {
    setSelectedPandits(prev => {
      const newSet = new Set(prev)
      if (newSet.has(panditId)) {
        newSet.delete(panditId)
      } else {
        newSet.add(panditId)
      }
      return newSet
    })
  }

  const toggleSelectAll = () => {
    if (selectedPandits.size === unverifiedPandits.length) {
      setSelectedPandits(new Set())
    } else {
      setSelectedPandits(new Set(unverifiedPandits.map(p => p.id)))
    }
  }

  const exportUnverifiedPandits = () => {
    const csvContent = "data:text/csv;charset=utf-8,"
      + "Name,Email,Phone,City,Location,Experience,Registration Date\n"
      + unverifiedPandits.map(p =>
        `"${p.name || p.profile?.name}","${p.email}","${p.phone}","${p.profile?.city || 'N/A'}","${p.location}","${p.experience}","${format(new Date(p.createdAt), 'yyyy-MM-dd')}"`
      ).join("\n")

    const encodedUri = encodeURI(csvContent)
    const link = document.createElement("a")
    link.setAttribute("href", encodedUri)
    link.setAttribute("download", `unverified_pandits_${format(new Date(), 'yyyy-MM-dd')}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast.success('Unverified pandits list exported successfully!')
  }

  // Get unique locations for filter
  const uniqueLocations = [...new Set(unverifiedPandits.map(p => p.location).filter(Boolean))]

  const getUrgencyLevel = (createdAt) => {
    const daysSinceRegistration = Math.floor((new Date() - new Date(createdAt)) / (1000 * 60 * 60 * 24))
    if (daysSinceRegistration > 7) return 'high'
    if (daysSinceRegistration > 3) return 'medium'
    return 'low'
  }

  const statCards = [
    {
      title: 'Pending Verification',
      value: stats.totalUnverified,
      icon: AlertTriangle,
      color: 'bg-red-500',
      change: stats.urgentReviews > 0 ? `${stats.urgentReviews} urgent` : 'All current',
      changeType: stats.urgentReviews > 0 ? 'attention' : 'neutral',
      description: 'Awaiting review'
    },
    {
      title: 'This Week',
      value: stats.pendingThisWeek,
      icon: TrendingUp,
      color: 'bg-blue-500',
      change: '+20%',
      changeType: 'positive',
      description: 'New applications'
    },
    {
      title: 'Avg Process Time',
      value: `${stats.avgProcessingTime} days`,
      icon: Clock,
      color: 'bg-green-500',
      change: '-0.5 days',
      changeType: 'positive',
      description: 'Review duration'
    },
    {
      title: 'Urgent Reviews',
      value: stats.urgentReviews,
      icon: User,
      color: 'bg-orange-500',
      change: stats.urgentReviews > 10 ? 'High queue' : 'Manageable',
      changeType: stats.urgentReviews > 10 ? 'attention' : 'neutral',
      description: 'Over 7 days old'
    }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            Unverified Pandits
          </h1>
          <p className="mt-2 text-gray-600">
            Review and verify pandit applications for platform approval
          </p>
          <div className="mt-1 text-sm text-red-600 font-medium">
            ⚠️ {stats.urgentReviews} applications require urgent attention
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          {selectedPandits.size > 0 && (
            <button
              onClick={handleBulkVerify}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors duration-200"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Verify Selected ({selectedPandits.size})
            </button>
          )}
          <button
            onClick={exportUnverifiedPandits}
            className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon
          return (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-16 h-16 opacity-5">
                <div className="w-full h-full bg-gradient-to-br from-red-400 to-orange-500 rounded-full transform translate-x-8 -translate-y-8"></div>
              </div>

              <div className="flex items-center justify-between relative z-10">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{stat.description}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg shadow-sm`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="mt-4 flex items-center">
                <span className={`text-sm font-medium ${stat.changeType === 'positive' ? 'text-green-600' :
                    stat.changeType === 'attention' ? 'text-orange-600' : 'text-gray-600'
                  }`}>
                  {stat.change}
                </span>
                <span className="text-sm text-gray-500 ml-2">status</span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search unverified pandits..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full sm:w-64 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            <select
              value={filterExperience}
              onChange={(e) => setFilterExperience(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="all">All Experience</option>
              <option value="0-2">0-2 years</option>
              <option value="3-7">3-7 years</option>
              <option value="8-15">8-15 years</option>
              <option value="15+">15+ years</option>
            </select>

            <select
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="all">All Locations</option>
              {uniqueLocations.map(location => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>
          </div>

          <div className="text-sm text-gray-600">
            Showing {pagination.total ? `${(pagination.page - 1) * pagination.limit + 1} - ${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total}` : '0'} applications
          </div>
        </div>
      </div>

      {/* Unverified Pandits Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 text-red-600" />
              Pending Verification Queue
            </h3>
            <p className="text-sm text-gray-600 mt-1">Review pandit applications and documents</p>
          </div>
          {unverifiedPandits.length > 0 && (
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={selectedPandits.size === unverifiedPandits.length && unverifiedPandits.length > 0}
                onChange={toggleSelectAll}
                className="rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <label className="text-sm text-gray-600">Select All</label>
            </div>
          )}
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading verification queue...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <div className="text-red-500 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Verification Queue</h3>
            <p className="text-gray-500 mb-4">{error}</p>
            <button
              onClick={fetchUnverifiedPandits}
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors duration-200"
            >
              Try Again
            </button>
          </div>
        ) : unverifiedPandits.length === 0 ? (
          <div className="p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-4" />
            <p className="text-gray-500">No pending verifications</p>
            <p className="text-sm text-gray-400 mt-2">All pandit applications have been processed</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedPandits.size === unverifiedPandits.length}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Applicant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact & Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Experience & Qualification
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Documents
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Applied
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {unverifiedPandits.map((pandit) => {
                  const urgency = getUrgencyLevel(pandit.createdAt)
                  const daysSinceApplication = Math.floor((new Date() - new Date(pandit.createdAt)) / (1000 * 60 * 60 * 24))

                  return (
                    <tr key={pandit.id} className={`hover:bg-gray-50 transition-colors duration-150 ${urgency === 'high' ? 'bg-red-50' : urgency === 'medium' ? 'bg-yellow-50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedPandits.has(pandit.id)}
                          onChange={() => toggleSelectPandit(pandit.id)}
                          className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-red-400 to-orange-500 flex items-center justify-center relative">
                              <span className="text-white font-semibold text-sm">
                                {(pandit.name || pandit.profile?.name)?.charAt(0)?.toUpperCase() || 'P'}
                              </span>
                              {urgency === 'high' && (
                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 rounded-full flex items-center justify-center">
                                  <AlertTriangle className="w-2 h-2 text-white" />
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 flex items-center">
                              {pandit.name || pandit.profile?.name || 'No name provided'}
                              {urgency === 'high' && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                  Urgent
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              {pandit.email}
                            </div>
                            {pandit.specializations && pandit.specializations.length > 0 && (
                              <div className="text-xs text-red-600 mt-1">
                                {pandit.specializations[0]}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          {pandit.phone && (
                            <div className="flex items-center text-sm text-gray-600">
                              <Phone className="w-3 h-3 mr-1" />
                              {pandit.phone}
                            </div>
                          )}
                          <div className="flex items-center text-sm text-gray-600">
                            <MapPin className="w-3 h-3 mr-1" />
                            {pandit.location}
                          </div>
                          <div className="text-xs text-gray-500">
                            City: {pandit.profile?.city || 'Not specified'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          <div className="text-sm text-gray-900 flex items-center">
                            <Clock className="w-3 h-3 mr-1 text-gray-400" />
                            {pandit.experience || 0} years
                          </div>
                          {pandit.isVerified !== undefined && (
                            <div className="text-xs text-gray-600 flex items-center">
                              <Award className="w-3 h-3 mr-1" />
                              Status: {pandit.isVerified ? 'Verified' : 'Unverified'}
                            </div>
                          )}
                          {pandit.specializations && pandit.specializations.length > 0 && (
                            <div className="text-xs text-orange-600">
                              {pandit.specializations.slice(0, 2).join(', ')}
                              {pandit.specializations.length > 2 && '...'}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          {pandit.documents?.map((doc, index) => (
                            <div key={index} className="flex items-center text-xs">
                              <FileText className="w-3 h-3 mr-1 text-gray-400" />
                              <span className="capitalize mr-1">{doc.type}:</span>
                              {doc.verified ? (
                                <CheckCircle className="w-3 h-3 text-green-500" />
                              ) : (
                                <XCircle className="w-3 h-3 text-red-500" />
                              )}
                            </div>
                          )) || (
                              <div className="text-xs text-red-600 flex items-center">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                No documents
                              </div>
                            )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center">
                          <Calendar className="w-3 h-3 mr-1 text-gray-400" />
                          {format(new Date(pandit.createdAt), 'MMM dd, yyyy')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {daysSinceApplication} days ago
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleViewDocuments(pandit.id)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50 transition-colors duration-150"
                            title="View Documents"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleVerifyPandit(pandit.id)}
                            disabled={verifying[pandit.id]}
                            className="text-green-600 hover:text-green-900 p-1 rounded-full hover:bg-green-50 transition-colors duration-150 disabled:opacity-50"
                            title="Verify Pandit"
                          >
                            {verifying[pandit.id] ? (
                              <Loader className="w-4 h-4 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4" />
                            )}
                          </button>
                          <div className="relative group">
                            <button className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-50 transition-colors duration-150">
                              <MoreHorizontal className="w-4 h-4" />
                            </button>
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                              <div className="py-1">
                                <button
                                  onClick={() => handleViewDocuments(pandit.id)}
                                  className="block w-full text-left px-4 py-2 text-sm text-blue-700 hover:bg-blue-50"
                                >
                                  <FileText className="w-4 h-4 inline mr-2" />
                                  View All Documents
                                </button>
                                <button
                                  onClick={() => handleVerifyPandit(pandit.id)}
                                  className="block w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-green-50"
                                >
                                  <CheckCircle className="w-4 h-4 inline mr-2" />
                                  Approve Application
                                </button>
                                {/* <button
                                  onClick={() => handleRequestMoreInfo(pandit.id)}
                                  disabled={requestingInfo[pandit.id]}
                                  className="block w-full text-left px-4 py-2 text-sm text-orange-700 hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {requestingInfo[pandit.id] ? (
                                    <Loader className="w-4 h-4 inline mr-2 animate-spin" />
                                  ) : (
                                    <Mail className="w-4 h-4 inline mr-2" />
                                  )}
                                  Request More Info
                                </button>
                                <button
                                  onClick={() => handleRejectApplication(pandit.id)}
                                  disabled={rejecting[pandit.id]}
                                  className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {rejecting[pandit.id] ? (
                                    <Loader className="w-4 h-4 inline mr-2 animate-spin" />
                                  ) : (
                                    <XCircle className="w-4 h-4 inline mr-2" />
                                  )}
                                  Reject Application
                                </button> */}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {unverifiedPandits.length > 0 && pagination.totalPages > 1 && (
          <div className="bg-white px-6 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={!pagination.hasPrev}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={!pagination.hasNext}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">
                      {(pagination.page - 1) * pagination.limit + 1}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.limit, pagination.total)}
                    </span>{' '}
                    of{' '}
                    <span className="font-medium">{pagination.total}</span>{' '}
                    results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={!pagination.hasPrev}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>

                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                      .filter(page => page === 1 || page === pagination.totalPages || Math.abs(page - currentPage) <= 1)
                      .map((page, index, array) => (
                        <React.Fragment key={page}>
                          {index > 0 && array[index - 1] !== page - 1 && (
                            <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                              ...
                            </span>
                          )}
                          <button
                            onClick={() => setCurrentPage(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === page
                                ? 'z-10 bg-red-50 border-red-500 text-red-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      ))}

                    <button
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      disabled={!pagination.hasNext}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default UnverifiedPandits